<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'
    import { DateTime } from 'luxon'

    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import PageList from 'hydria/components/PageList.svelte'

    const LIMIT = 10
    const DELAY_MS = 1250

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        skip: 'int1+',
        active: '3state',
    }
    const mappingOrder = ['q', 'skip']

    // Search options for the backend
    interface SOptions {
        q: string
        skip: number
        active: null | boolean
    }
    const defaults: SOptions = {
        q: '',
        skip: 0,
        active: null,
    }

    interface ISearchItem {
        id: string
        ctime: DateTime
        mtime: DateTime
        email: string
        active: boolean | null
        locked: boolean
        email_md5: string
        metadata: {
            name?: string
            prefered_lg: 'en' | 'fr'
        }
    }

    // Search result from the server
    interface ISearchResult {
        items: ISearchItem[]
        total_items: number
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/admin-users' : `/admin-users?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/admin-users' : `/admin-users?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string | undefined) {
        let sMap = rt.simpleParseQs(qs ? qs : '', rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    let results = writable([] as any[])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = {}

        if (sOptions.q != '') sdata.text = sOptions.q
        for (const [field, value] of Object.entries(sOptions)) {
            if (['q', 'skip'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (value != null && value != '') sdata[field] = value
                    break
            }
        }
        sdata.limit = LIMIT
        sdata.sort_by = 'email'
        sdata.sort_asc = true
        if (sOptions.skip > 0) sdata.skip = sOptions.skip

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/users/1/', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []
        const now = DateTime.now()

        nItems = data.total_items

        for (const item of data.items) {
            for (const k of ['ctime', 'mtime']) if (item[k]) item[k] = toLuxon(item[k])

            litems.push(item)
        }

        results.set(litems)
        if (loading) loading = false
    }

    let text = writable(sOptions.q)

    function loadIntoForm() {
        $text = sOptions.q
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.q) {
                return
            }
            textTimer = setTimeout(() => {
                sOptions.q = newValue
                textTimer = null
                setQueryString(sOptions)
            }, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()

        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    const crumbs = [{ txt: tgettext('Utilisateurs du site administratif'), url: spath('/admin-users') }]
</script>

<style>
    .text-very-muted {
        color: #eee;
    }
</style>

<Breadcrumb {crumbs} />

<div class="card mb-4">
    <div class="card-header text-bg-primary">{$_('Search parameters')}</div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
        >
            <div style="display: flex; gap: 0.5em">
                <div style="flex: 1 0 0">
                    <div class="input-group">
                        <input type="text" class="form-control" bind:this={mainInput} bind:value={$text} />
                        {#if $text}
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                disabled={updating}
                                onclick={() => {
                                    $text = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                        <button class="btn btn-secondary" type="submit" disabled={updating}>{$_('Filter')}</button>
                    </div>
                </div>
                <div style="flex: 0 0 auto">
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-left"
                            data-tooltip="Compte actif (peut se connecter)"
                            disabled={updating}
                            class:active={sOptions.active != null && sOptions.active == true}
                            onclick={() => {
                                sOptions.active = sOptions.active == true ? null : true
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:unlock-alt" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary tt-left"
                            data-tooltip="Compte inactif (ne peut pas se connecter)"
                            disabled={updating}
                            class:active={sOptions.active != null && sOptions.active == false}
                            onclick={() => {
                                sOptions.active = sOptions.active != null && sOptions.active == false ? null : false
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:lock" /></button
                        >
                    </div>
                </div>
            </div>
        </form>
    </div>
</div>

{#if loading}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card">
        <div class="card-header text-bg-primary">{$_('Utilisateurs administratifs')}</div>

        {#if $results.length}
            <div class="table-responsive">
                <table class="admin-users table table-striped mb-0" class:disabled-look={updating}>
                    <thead>
                        <tr>
                            <th>{$_('Name')}</th>
                            <th>{$_('Email address')}</th>
                            <th class="text-center">Actif</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $results as item (item.id)}
                            <tr>
                                <td><a use:sanchor href={spath(`/admin-user/${item.id}`)}>{item.metadata && item.metadata.name ? item.metadata.name : '<not set>'}</a></td>
                                <td>{item.email}</td>
                                <td class="text-center">
                                    {#if item.active}
                                        <span>Actif</span>
                                    {:else}
                                        <span class="text-muted">Inactif</span>
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
        <div class="card-footer text-bg-light">
            <PageList
                loading={updating}
                {nItems}
                limit={LIMIT}
                skip={sOptions.skip}
                setSkip={(e) => {
                    sOptions.skip = e
                    setQueryString(sOptions)
                }}
            />
        </div>
        <div class="card-footer">
            <a use:sanchor href={spath('/admin-user/new')} class="btn btn-primary">{$_('Create a new user')}</a>
        </div>
    </div>
{/if}
